<template>
  <div
    class="device-card"
    :class="[
      `device-card--${size}`,
      {
        'device-card--on': active,
        'device-card--accent': Boolean(accent),
        'device-card--filled': fill,
        'device-card--breathing': breathing && active,
      },
    ]"
    :style="accent ? { '--card-accent': accent } : undefined"
    :title="title">
    <span class="device-card__media">
      <slot name="media" />
      <!-- Top-left of the picture: a reading that belongs to the device itself
           (an appliance's remaining time), as opposed to the automation stack
           the page hangs in the opposite corner. -->
      <span v-if="$slots.corner" class="device-card__corner"><slot name="corner" /></span>
    </span>

    <span class="device-card__name"
      ><span class="device-card__name-text">{{ name }}</span></span
    >

    <slot name="value" />

    <!-- The readings beside the headline (a cavity temperature, a CPU load) —
         one quiet row, present from the middle size up. -->
    <span v-if="size !== 'min' && $slots.facts" class="device-card__facts"><slot name="facts" /></span>

    <!-- The controls exist only at the largest size: a min card is a picture and
         a name, a mid card adds the reading, and a max card is the only one with
         room for a slider or a verb. A press inside the row never reaches the
         card's own tap. -->
    <span v-if="size === 'max'" class="device-card__control" @pointerdown.stop @click.stop @contextmenu.stop>
      <slot name="control" />
    </span>
  </div>
</template>

<script setup lang="ts">
/**
 * The one card face every device widget borrows: a media area, a two-line name,
 * an optional reading, and — at the largest size only — the device's own
 * controls. Metrics come from the page's grid (`--card-*`), so the same card
 * follows the size toggle without knowing the page it sits on.
 *
 * The root is the tap target; the owner spreads `useGridTap`'s handlers on it
 * (`@click`, the pointer pair) exactly as it does on its own markup.
 */
export type DeviceCardSize = 'min' | 'mid' | 'max';

withDefaults(
  defineProps<{
    size: DeviceCardSize;
    name: string;
    title?: string;
    /** Lit / open / active — the card's accent border and fill. */
    active?: boolean;
    /** The device's own colour, mixed into the fill while it is active. */
    accent?: string | null;
    /**
     * The accent fills the CARD, not just its edge. Lamps own this language —
     * a light's colour is its face — while everything else keeps the toned
     * surface below (owner, 25.10.2026: *"no filled bgs, use color borders and
     * glow,, slight bg … colored bgs for lamps only"*).
     */
    fill?: boolean;
    /**
     * The card is a machine at work: while it is active the accent glow eases
     * in and out, so a running appliance reads as running from across the room
     * (owner, 25.10.2026: *"when device is on it should be breathing"*). Only
     * the surfaces that describe a load turn it on — a lamp has its own light.
     */
    breathing?: boolean;
  }>(),
  { title: undefined, active: false, accent: null, fill: false, breathing: false },
);
</script>

<style scoped>
.device-card {
  /* Every page that draws these cards sits on the app's own gray canvas
     (`bg-gray-100 dark:bg-gray-900`) — the same colour the old fill used, so an
     off card had no visible surface and its border was transparent. The card
     owns an opaque fill and one edge in both themes (owner, 23.10.2026: *"the
     off light has no bg.. need default bg and border"*). Pages can still
     override the three variables for a different canvas. */
  --card-base: #ffffff;
  --card-edge: rgb(var(--gray-200));
  --card-hover: rgb(var(--gray-100));
  /* The one ink the readings inside a card use, so a child never has to know
     whether its card is lit, toned or plain. */
  --card-ink: #64748b;
  display: flex;
  min-width: 0;
  height: 100%;
  flex-direction: column;
  align-items: stretch;
  gap: 0.25rem;
  /* The grid sets these per card size (`lt-tiles--min|mid|max`). */
  padding: var(--card-pad, 0.5rem 0.45rem 0.45rem);
  border: 1px solid var(--card-edge);
  border-radius: 0.9rem;
  background: var(--card-base);
  cursor: pointer;
  touch-action: pan-y;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
}

.dark .device-card {
  --card-base: rgb(var(--gray-800));
  --card-edge: rgb(var(--gray-700));
  --card-hover: rgb(var(--gray-700));
  --card-ink: #94a3b8;
}

.device-card:hover {
  background: var(--card-hover);
}

/* ── A toned card, not a filled one ─────────────────────────────────────────
   The accent speaks through the EDGE and a soft glow; the surface keeps the
   card's own base with the faintest wash of the tone (owner, 25.10.2026:
   *"apply same styling (no filled bgs, use color borders and glow,, slight bg)
   on climate widgets, Appliances, Climate, Wellness, Energy, Water.. colored
   bgs for lamps only"*). The filled language survives as `.device-card--filled`
   below, for lamps alone. */
.device-card--on {
  border-color: color-mix(in srgb, var(--card-accent, rgb(251 191 36)) 45%, var(--card-edge));
  background: color-mix(in srgb, var(--card-accent, rgb(251 191 36)) 5%, var(--card-base));
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--card-accent, rgb(251 191 36)) 30%, transparent),
    0 0 0.65rem color-mix(in srgb, var(--card-accent, rgb(251 191 36)) 22%, transparent);
}

/* A device that is merely reporting (a sensor, an unknown appliance): a hint of
   its tone and the same soft edge, never a filled card. */
.device-card--accent {
  border-color: color-mix(in srgb, var(--card-accent) 26%, var(--card-edge));
  background: color-mix(in srgb, var(--card-accent) 4%, var(--card-base));
}

.device-card--accent.device-card--on {
  border-color: color-mix(in srgb, var(--card-accent) 55%, var(--card-edge));
  background: color-mix(in srgb, var(--card-accent) 6%, var(--card-base));
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--card-accent) 38%, transparent),
    0 0 0.75rem color-mix(in srgb, var(--card-accent) 26%, transparent);
}

/* ── The filled card (lamps) ────────────────────────────────────────────────
   A lamp's colour IS its face: half the fill is the tone, so a red lamp and a
   green one are told apart from across the room. Everything else stays toned. */
.device-card--filled.device-card--on {
  border-color: color-mix(in srgb, var(--card-accent, rgb(251 191 36)) 42%, var(--card-edge));
  background: color-mix(in srgb, var(--card-accent, rgb(251 191 36)) 16%, var(--card-base));
}

.device-card--filled.device-card--accent.device-card--on {
  border-color: color-mix(in srgb, var(--card-accent) 78%, var(--card-edge));
  background: color-mix(in srgb, var(--card-accent) 46%, var(--card-base));
  box-shadow: 0 0 0.9rem color-mix(in srgb, var(--card-accent) 30%, transparent);
  /* The fill is half the tone, so the ink flips to the theme's own: dark on
     the light tint, light on the dark one. */
  --card-ink: #1e293b;
}

.dark .device-card--filled.device-card--accent.device-card--on {
  --card-ink: #e2e8f0;
}

.device-card__media {
  position: relative;
  display: flex;
  height: var(--card-media, 6.5rem);
  flex: none;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: rgb(148, 163, 184);
}

/* The picture's own corner: a reading that belongs to the device, kept apart
   from the automation stack the page hangs in the opposite one. */
.device-card__corner {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  pointer-events: none;
}

/* Every drawing a widget puts here (sensor, cover, fan, appliance art) is an
   SVG that should fill the box rather than sit at its own intrinsic size. */
.device-card__media :deep(svg) {
  width: 100%;
  height: 100%;
}

/* …and every PICTURE here is an icon, not a photo (owner, 25.10.2026: "should
   display images iconic", "not cover").
   Painted the way a `div` with `background-size: contain; background-position:
   center` would be — the picture is taken out of the flow and pinned to the
   middle of the media box, so no widget's own width/align rules can leave it
   off-centre or clipped. The owner's own suggestion (25.10.2026) was the
   background approach; this is the same result without moving fifteen widgets
   from <img> to background-image. */
.device-card__media :deep(img) {
  position: absolute;
  inset: 0;
  margin: auto;
  width: auto;
  height: auto;
  /* No inset: the owner asked for the artwork to go as large as the box allows
     (25.10.2026: "images are a bit too small.. remove padding or empty space").
     The cut-outs already carry their own ~12 % transparent margin, so the product
     still reads inset without the card adding a second one. */
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  object-position: center;
}

.device-card--on .device-card__media {
  color: color-mix(in srgb, var(--card-accent, rgb(251 191 36)) 70%, #0f172a);
}

/* Two lines' worth of box on every card, so a short name and a long one leave
   the cards the same height — and a one-line name sits centred in it. */
.device-card__name {
  display: flex;
  height: 2.3em;
  flex: none;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: var(--card-name, 0.85rem);
  font-weight: 600;
  line-height: 1.2;
  color: #4b5563;
  text-align: center;
}

.device-card__name-text {
  display: -webkit-box;
  overflow: hidden;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.device-card--on .device-card__name {
  color: color-mix(in srgb, var(--card-accent, rgb(251 191 36)) 55%, #111827);
}

/* A filled card's fill is half its tone, so the ink is the shell's own — one
   variable that flips with the theme. It out-ranks the plain `--on` mix so a
   name never reads as a darker shade of the fill it sits on. */
.device-card--filled.device-card--accent.device-card--on .device-card__name,
.dark .device-card--filled.device-card--accent.device-card--on .device-card__name {
  color: var(--card-ink);
}

.dark .device-card__name {
  color: #cbd5e1;
}

.dark .device-card--on .device-card__name {
  color: color-mix(in srgb, var(--card-accent, rgb(251 191 36)) 60%, #f8fafc);
}

.device-card__control {
  display: flex;
  flex: none;
  align-items: center;
  gap: 0.3rem;
  cursor: default;
}

/* ── The readings beside the headline ───────────────────────────────────── */
.device-card__facts {
  display: flex;
  flex: none;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  overflow: hidden;
  font-size: var(--card-fact, 0.68rem);
  font-variant-numeric: tabular-nums;
  color: var(--card-ink);
}

/* ── Breathing ──────────────────────────────────────────────────────────────
   A machine at work: the accent glow swells and settles instead of holding
   still, and the picture itself rises a touch with it. A few percent of scale
   is what makes the card read as *alive* from across the room; the border and
   the ring stay exactly where they were. Motion-sensitive users get the steady
   lit card they had before. */
.device-card--breathing {
  animation: device-card-breathe 3.6s ease-in-out infinite;
}

.device-card--breathing .device-card__media {
  animation: device-card-media-breathe 3.6s ease-in-out infinite;
}

@keyframes device-card-breathe {
  0%,
  100% {
    box-shadow: 0 0 0.3rem color-mix(in srgb, var(--card-accent, rgb(251 191 36)) 14%, transparent);
  }
  50% {
    box-shadow: 0 0 1.25rem color-mix(in srgb, var(--card-accent, rgb(251 191 36)) 48%, transparent);
  }
}

@keyframes device-card-media-breathe {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.035);
  }
}

@media (prefers-reduced-motion: reduce) {
  .device-card--breathing {
    animation: none;
    box-shadow: 0 0 0.6rem color-mix(in srgb, var(--card-accent, rgb(251 191 36)) 26%, transparent);
  }

  .device-card--breathing .device-card__media {
    animation: none;
  }
}
</style>
